import React, { useState, useEffect, useRef } from "react";
import { message } from "antd";
import { useDispatch } from "react-redux";
import * as TrustedDealerService from "../services/TrustedDealer";

const TrustedDealerCocGuard = ({ profileId, initialCocData, onAgreedSuccess, isPreviewMode = false, onClosePreview }) => {
  const dispatch = useDispatch();
  const [modalVisible, setModalVisible] = useState(false);
  const [cocData, setCocData] = useState(null);
  const contentRef = useRef(null);

  // Sync pre-loaded initialCocData if passed
  useEffect(() => {
    if (initialCocData) {
      const rawContent = initialCocData.system_content || '';
      const sysContent = rawContent.replace(/\\'/g, "'").replace(/\\"/g, '"');
      setCocData({
        agreed: initialCocData.agreed,
        agreedVersion: initialCocData.version,
        sysVersion: initialCocData.system_version || '1.0',
        sysContent: sysContent,
        needsReagree: initialCocData.needs_reagree
      });
    }
  }, [initialCocData]);

  const fetchCocStatus = () => {
    dispatch(TrustedDealerService.getTrustedDealerProfileData(profileId || 0))
      .then((res) => {
        if (res) {
          const profileData = (res && res.data && typeof res.data === 'object' && !res.profile_id) ? res.data : res;
          const sysVersion = profileData.system_coc_version || '1.0';
          const rawContent = profileData.system_coc_content || '';
          const sysContent = rawContent.replace(/\\'/g, "'").replace(/\\"/g, '"');
          const agreed = !!profileData.coc_agreed;
          const agreedVersion = profileData.coc_agreed_version || '';

          const needsReagree = profileData.needs_coc_reagree !== undefined 
            ? !!profileData.needs_coc_reagree 
            : (!agreed || agreedVersion !== sysVersion);

          setCocData({ agreed, agreedVersion, sysVersion, sysContent, needsReagree });
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (isPreviewMode) {
      setModalVisible(true);
      if (cocData?.needsReagree || !cocData) {
        dispatch(TrustedDealerService.agreeCodeOfConduct())
          .then(() => {
            if (onAgreedSuccess && typeof onAgreedSuccess === 'function') {
              onAgreedSuccess();
            }
          })
          .catch(() => {});
      }
    } else {
      setModalVisible(false);
    }
    fetchCocStatus();
  }, [profileId, isPreviewMode]);

  useEffect(() => {
    if (modalVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [modalVisible]);

  const handleClose = () => {
    setModalVisible(false);
    if (onClosePreview && typeof onClosePreview === 'function') {
      onClosePreview();
    }
  };

  if (!modalVisible) return null;

  const ver = cocData?.sysVersion || '1.0';

  return (
    <div className="fdcoc-backdrop" onClick={handleClose}>
        <div className="fdcoc-card" onClick={e => e.stopPropagation()}>

          {/* ══════ HEADER ══════ */}
          <div className="fdcoc-header">
            <div className="fdcoc-header-dots">
              {Array.from({length:15}).map((_,i) => <span key={i}/>)}
            </div>
            <div className="fdcoc-header-row">
              <div className="fdcoc-icon-wrap">
                <svg viewBox="0 0 24 24">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <div className="fdcoc-header-info">
                <h2>Floor Detective — Trusted Dealer</h2>
                <p>Code of Conduct & Participation Standards</p>
              </div>
              <div className="fdcoc-version-badge">
                <span className="fdcoc-version-dot" />
                v{ver}
              </div>
            </div>
          </div>

          {/* ══════ INFO BAR ══════ */}
          <div className="fdcoc-alert">
            <div className="fdcoc-alert-badge">
              <svg viewBox="0 0 24 24">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
            </div>
            <div className="fdcoc-alert-msg">
              <strong>Participation Standards (Version {ver})</strong> — Review the active network standards and code of conduct guidelines below.
            </div>
          </div>

          {/* ══════ BODY ══════ */}
          <div className="fdcoc-body" ref={contentRef}>
            <div dangerouslySetInnerHTML={{ __html: cocData?.sysContent || '' }} />
          </div>

          {/* ══════ FOOTER ══════ */}
          <div className="fdcoc-footer">
            <button className="fdcoc-close-btn" onClick={handleClose}>
              ✓ Close Standards
            </button>
          </div>

        </div>
      </div>
  );
};

export default TrustedDealerCocGuard;
